import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { ModelOrderResponse, ModelPaymentPlanResponse } from '@layup/api-sdk';
import { NetworkError } from '../errors';
import { fetchOrders, getOrderByIdWithPlansPaymentsAndConnectors } from '../services/orders';
import { addMetric, createException, createWarning } from '../utils/monitoring.utils';

export const useOrderStore = defineStore('orders', {
  state: () => {
    const orders = ref<ModelOrderResponse[]>();
    const allOrders = ref<ModelOrderResponse[]>([]);
    const allOrdersFetched = ref<boolean>(false);
    const allOrdersLoading = ref<boolean>(false);
    const order = ref<ModelOrderResponse | undefined>(undefined);
    const loading = ref<boolean>(false);
    const error = ref<string | undefined>(undefined);
    return { orders, allOrders, allOrdersFetched, allOrdersLoading, order, loading, error };
  },
  actions: {
    getPaymentPlan(): ModelPaymentPlanResponse | undefined {
      if (!this.order) {
        return undefined;
      }
      const plan = this.order?.plans?.[0];
      return plan;
    },
    async fetchAllOrders(force: boolean = false) {
      if (this.allOrdersFetched && !force) return;
      this.allOrdersLoading = true;
      try {
        const { orders } = await fetchOrders({
          populate: ['voucher', 'plans', 'plans.benefactor', 'supplier', 'plans.payments'],
        });
        this.allOrders = orders || [];
        this.allOrdersFetched = true;
      } catch (error) {
        if (error instanceof NetworkError) {
          createWarning(error);
          addMetric('shopper-home', { status: 'network_error', channel: 'shopper' });
        } else {
          createException(error);
          addMetric('shopper-home', { status: 'error', channel: 'shopper' });
        }
      } finally {
        this.allOrdersLoading = false;
      }
    },
    async getOrderById(orderId: string) {
      try {
        this.order = undefined;
        this.error = undefined;
        this.loading = true;
        this.order = await getOrderByIdWithPlansPaymentsAndConnectors(orderId);
        addMetric('order', { status: 'success', channel: 'merchant' });
      } catch (error) {
        if (error instanceof NetworkError) {
          createWarning(error);
          addMetric('order', { status: 'network_error', channel: 'merchant' });
        } else {
          createException(error);
          addMetric('order', { status: 'error', channel: 'merchant' });
        }
        this.error = (error as Error).message;
      } finally {
        this.loading = false;
      }
    },
    setOrders(orders: ModelOrderResponse[]) {
      this.orders = orders;
    },
    updateOrder(order: ModelOrderResponse) {
      this.order = order;
      const index = this.orders?.findIndex((o) => o._id === order._id);
      if (index !== undefined && index !== -1 && this.orders) {
        this.orders[index] = order;
      }
    },
    reset() {
      this.$reset();
    },
  },
  persist: {
    afterRestore: (ctx) => {
      ctx.store.allOrdersFetched = false;
    },
  },
});
